// ---------------------------------------------------------------------------
// Test Mode — daily & weekly summaries + calendar/email/chat (CLE-250).
//
// These reuse the simulated tasks and people so the per-person breakdowns stay
// coherent with the Tasks board and the People directory.
// ---------------------------------------------------------------------------

import type {
  DailySummary,
  BusinessDailySummary,
  DailyTask,
  DailyCalendarEvent,
  DailyRecommendation,
  GroupedTasks,
  TeamUser,
} from '../lib/dailyApi';
import type {
  WeeklySummary,
  WeeklyStatus,
  WeeklyStatusGroup,
  WeeklyUserSummary,
  WeeklyDay,
} from '../lib/weeklyApi';
import type { EmailSummary } from '../lib/emailApi';
import type { CommentAttachment } from '../lib/tasksApi';
import type {
  CheckInStatus,
  DailyReportHistoryItem,
  DailyStatusBoard,
  DailyStatusReport,
  DailyStatusSettings,
  ReportSection,
  ReportTask,
  TeamDailyStatusReport,
  TodayTask,
} from '../lib/dailyStatusApi';
import type { ConversationSummary, Conversation, ChatMessage } from '../lib/chatApi';
import { isoDate, isoDateTime } from './company';
import { buildTasks } from './tasks';
import { buildPeople, PEOPLE_REFS } from './people';

const PRIORITY_NUM: Record<string, number> = {
  URGENT: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
  NONE: 0,
};

function toDailyTask(t: ReturnType<typeof buildTasks>[number]): DailyTask {
  return {
    id: t.id,
    identifier: t.identifier,
    title: t.title,
    description: t.description,
    // The daily backend sends the status display name (task.status.name).
    status: t.statusLabel,
    statusId: t.statusId,
    priority: PRIORITY_NUM[t.priority] ?? 0,
    priorityLevel: t.priority,
    statusLabel: t.statusLabel,
    statusColor: t.statusColor,
    statusCategory: t.statusCategory,
    dueDate: t.dueDate,
    projectId: t.projectId,
    projectName: t.projectName,
    assignee: t.assignee,
    url: null,
    source: 'project',
  };
}

function groupTasks(tasks: DailyTask[]): GroupedTasks {
  const today = isoDate(0);
  const tomorrow = isoDate(1);
  return {
    overdue: tasks.filter((t) => t.dueDate != null && t.dueDate < today).slice(0, 4),
    today: tasks.filter((t) => t.dueDate === today).slice(0, 8),
    tomorrow: tasks.filter((t) => t.dueDate === tomorrow).slice(0, 5),
    next: tasks.filter((t) => t.dueDate != null && t.dueDate > tomorrow).slice(0, 10),
  };
}

// --- Fixed-date calendar (CLE-269) -----------------------------------------
// Some scripted demos pin a specific day (e.g. the Salesman Conference on
// 19 June 2026). Build ISO timestamps for an explicit calendar date so the
// events show up only when that exact day is selected, independent of the
// rolling "test today" anchor used by the generic events above.

function fixedDateTime(date: string, hour: number, minute = 0): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d, hour, minute, 0, 0).toISOString();
}

// Event category drives the UI color coding (CLE-269). Carried on the
// DailyCalendarEvent via a synthetic `calendarName` the renderer maps to a
// palette: external conference (purple), speech (green), team (slate),
// admin/check (amber), personal (default).
const SALESMAN_CONFERENCE_DATE = '2026-06-19';

const FIXED_DATE_EVENTS: Record<string, DailyCalendarEvent[]> = {
  [SALESMAN_CONFERENCE_DATE]: [
    {
      id: 'conf-2026-06-19-check-in',
      googleEventId: null,
      calendarId: 'cal-admin',
      calendarName: 'Admin',
      summary: 'Check înregistrare Conferința Salesman',
      description: 'Verificare acreditări, badge, materiale prezentare.',
      location: 'Recepție conferință',
      startTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 8, 0),
      endTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 8, 30),
      allDay: false,
      status: 'confirmed',
      color: null,
    },
    {
      id: 'conf-2026-06-19-main',
      googleEventId: null,
      calendarId: 'cal-extern',
      calendarName: 'Conferință',
      summary: 'Conferința Salesman',
      description: 'Eveniment full-day — networking, sesiuni, workshopuri.',
      location: 'Centrul de Conferințe',
      startTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 9, 0),
      endTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 23, 30),
      allDay: false,
      status: 'confirmed',
      color: null,
    },
    {
      id: 'conf-2026-06-19-speech',
      googleEventId: null,
      calendarId: 'cal-speech',
      calendarName: 'Speech',
      summary: 'Speech pe scena la Conferința Salesman',
      description: 'Prezentare/speech pe scena principală.',
      location: 'Scena principală',
      startTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 13, 0),
      endTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 14, 0),
      allDay: false,
      status: 'confirmed',
      color: null,
    },
    {
      id: 'conf-2026-06-19-team-check',
      googleEventId: null,
      calendarId: 'cal1',
      calendarName: 'Echipă',
      summary: 'Team Check',
      description: 'Sync rapid cu echipa CCC — statusuri proiecte.',
      location: 'Online',
      startTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 15, 0),
      endTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 15, 30),
      allDay: false,
      status: 'confirmed',
      color: null,
    },
    {
      id: 'conf-2026-06-19-retro',
      googleEventId: null,
      calendarId: 'cal-admin',
      calendarName: 'Admin',
      summary: 'Retro înscrieri Conferința Salesman',
      description: 'Analiza înscrierilor și participanților la conferință.',
      location: 'Sala VIP',
      startTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 16, 0),
      endTime: fixedDateTime(SALESMAN_CONFERENCE_DATE, 16, 30),
      allDay: false,
      status: 'confirmed',
      color: null,
    },
  ],
};

const CALENDAR_EVENTS: DailyCalendarEvent[] = [
  {
    id: 'ev1',
    googleEventId: null,
    calendarId: 'cal1',
    calendarName: 'Echipă',
    summary: 'Sprint planning',
    description: 'Planificare sprint nou',
    location: 'Sala Mare',
    startTime: isoDateTime(0, 9),
    endTime: isoDateTime(0, 10),
    allDay: false,
    status: 'confirmed',
    color: null,
  },
  {
    id: 'ev2',
    googleEventId: null,
    calendarId: 'cal1',
    calendarName: 'Clienți',
    summary: 'Meet Ion Popescu - CEO Mega Industries',
    description: 'Status update Corporate Website',
    location: 'Google Meet',
    startTime: isoDateTime(0, 11),
    endTime: isoDateTime(0, 12),
    allDay: false,
    status: 'confirmed',
    color: null,
  },
  {
    id: 'ev3',
    googleEventId: null,
    calendarId: 'cal1',
    calendarName: 'Echipă',
    summary: '1:1 cu Ioana Pop',
    description: null,
    location: 'Birou',
    startTime: isoDateTime(0, 13),
    endTime: isoDateTime(0, 14),
    allDay: false,
    status: 'confirmed',
    color: null,
  },
  {
    id: 'ev4',
    googleEventId: null,
    calendarId: 'cal1',
    calendarName: 'Clienți',
    summary: 'Demo Mobile Banking App',
    description: 'Demo cu FinTech Partners',
    location: 'Zoom',
    startTime: isoDateTime(0, 15),
    endTime: isoDateTime(0, 16),
    allDay: false,
    status: 'confirmed',
    color: null,
  },
  {
    id: 'ev5',
    googleEventId: null,
    calendarId: 'cal1',
    calendarName: 'Echipă',
    summary: 'Retrospectivă sprint',
    description: null,
    location: 'Sala Mică',
    startTime: isoDateTime(0, 16, 30),
    endTime: isoDateTime(0, 17, 30),
    allDay: false,
    status: 'confirmed',
    color: null,
  },
  {
    id: 'ev6',
    googleEventId: null,
    calendarId: 'cal1',
    calendarName: 'Personal',
    summary: 'Review propunere TradeHub',
    description: 'Pregătire ofertă marketplace B2B',
    location: 'Birou',
    startTime: isoDateTime(0, 18),
    endTime: isoDateTime(0, 19),
    allDay: false,
    status: 'confirmed',
    color: null,
  },
];

// Calendar recommendations are only authored for the conference owner (p1) and
// the scripted day. They are intentionally specific to that agenda (CLE-269).
function buildRecommendations(userId: string, date: string): DailyRecommendation[] {
  if (userId !== 'p1' || date !== SALESMAN_CONFERENCE_DATE) return [];
  return [
    {
      id: 'rec-prep-speech',
      kind: 'prep',
      title: 'Pregătire speech',
      body: 'Ai speech la 13:00. Recomandăm să revizuiești slide-urile între 11:30–12:30 și să faci o pauză de 15 min înainte.',
    },
    {
      id: 'rec-time-management',
      kind: 'time',
      title: 'Time management',
      body: 'Ziua este foarte încărcată (08:00–23:30). Planifică pauze scurte la 10:30 și 16:30 pentru a menține energia.',
    },
    {
      id: 'rec-team-sync',
      kind: 'team',
      title: 'Team sync',
      body: 'Team Check la 15:00 — pregătește un update scurt pe proiectele critice: RetuRO, HoReCa, ADNV.',
    },
    {
      id: 'rec-networking',
      kind: 'networking',
      title: 'Networking',
      body: 'Conferința Salesman e oportunitate de networking. Identifică 3–5 persoane cheie de contactat.',
    },
    {
      id: 'rec-follow-up',
      kind: 'follow_up',
      title: 'Follow-up',
      body: 'După speech, colectează feedback și contacte. Trimite follow-up email în 24h.',
    },
  ];
}

export function buildDailySummary(userId: string, date?: string): DailySummary {
  const person = buildPeople().find((p) => p.id === userId) ?? buildPeople()[0];
  const selectedDate = date ?? isoDate(0);
  // Pinned events for a scripted calendar day (e.g. 19 June 2026) take over;
  // otherwise fall back to the rolling generic agenda anchored on "today".
  const fixed = FIXED_DATE_EVENTS[selectedDate];
  const events = fixed ?? (selectedDate === isoDate(0) ? CALENDAR_EVENTS : []);
  const recommendations = buildRecommendations(person.id, selectedDate);
  const myTasks = buildTasks()
    .filter((t) => t.assigneeId === person.id)
    .map(toDailyTask);
  const grouped = groupTasks(myTasks.length ? myTasks : buildTasks().slice(0, 12).map(toDailyTask));
  const byProjectMap = new Map<string, DailyTask[]>();
  for (const t of myTasks) {
    const key = t.projectId ?? 'none';
    byProjectMap.set(key, [...(byProjectMap.get(key) ?? []), t]);
  }
  return {
    user: { id: person.id, name: person.fullName, email: person.email, role: 'MEMBER' },
    date: selectedDate,
    calendar: { events, totalEvents: events.length },
    recommendations,
    tasks: {
      grouped,
      byProject: Array.from(byProjectMap.entries()).map(([projectId, tasks]) => ({
        projectId: projectId === 'none' ? null : projectId,
        projectName: tasks[0]?.projectName ?? 'Fără proiect',
        tasks,
      })),
      statuses: ['De făcut', 'În lucru', 'În review', 'Finalizat'],
      total: myTasks.length,
    },
  };
}

export function buildBusinessDailySummary(): BusinessDailySummary {
  const people = buildPeople();
  return {
    date: isoDate(0),
    users: people.map((person) => {
      const myTasks = buildTasks()
        .filter((t) => t.assigneeId === person.id)
        .map(toDailyTask);
      return {
        user: {
          id: person.id,
          name: person.fullName,
          email: person.email,
          role: 'MEMBER',
          avatarUrl: null,
        },
        grouped: groupTasks(myTasks),
        total: myTasks.length,
      };
    }),
  };
}

export function buildTeamUsers(): TeamUser[] {
  return buildPeople().map((p) => ({
    id: p.id,
    name: p.fullName,
    email: p.email,
    role: 'MEMBER',
    avatarUrl: null,
  }));
}

// --- Weekly ----------------------------------------------------------------

const WEEKLY_STATUSES: WeeklyStatus[] = ['DONE', 'IN_PROGRESS', 'IN_REVIEW', 'TODO', 'BLOCKED'];

// Task statuses are now slugs (configurable statuses); map them onto the
// weekly buckets the same way the backend resolver does.
function toWeeklyStatus(s: string): WeeklyStatus {
  if (s === 'cancelled') return 'CANCELLED';
  const upper = s.toUpperCase();
  if (WEEKLY_STATUSES.includes(upper as WeeklyStatus)) return upper as WeeklyStatus;
  return 'TODO';
}

export function buildWeeklySummary(): WeeklySummary {
  const people = buildPeople();
  const allTasks = buildTasks();

  const users: WeeklyUserSummary[] = people.slice(0, 12).map((person) => {
    const personTasks = allTasks.filter((t) => t.assigneeId === person.id).slice(0, 6);
    const byStatus = new Map<WeeklyStatus, typeof personTasks>();
    for (const t of personTasks) {
      const st = toWeeklyStatus(t.status);
      byStatus.set(st, [...(byStatus.get(st) ?? []), t]);
    }
    const groups: WeeklyStatusGroup[] = Array.from(byStatus.entries()).map(([status, tasks]) => ({
      status,
      tasks: tasks.map((t) => ({
        id: t.id,
        identifier: t.identifier,
        title: t.title,
        url: null,
        priority: PRIORITY_NUM[t.priority] ?? 0,
        priorityLevel: t.priority,
        dueDate: t.dueDate,
        statusLabel: t.statusLabel,
        statusColor: t.statusColor,
        statusCategory: t.statusCategory,
        projectId: t.projectId,
        projectName: t.projectName,
        assignee: t.assignee,
        source: 'project' as const,
        status,
        createdAt: t.createdAt,
        changes: [{ at: isoDateTime(-2, 11), from: 'TODO', to: status }],
      })),
    }));
    return {
      user: {
        id: person.id,
        name: person.fullName,
        email: person.email,
        role: 'MEMBER',
        avatarUrl: null,
      },
      groups,
      total: personTasks.length,
    };
  });

  // The day timeline points at real demo tasks (four finished ones per day,
  // rotating), so clicking a row opens that task like it does live. Made-up
  // ids here would land on "task not found".
  const finished = allTasks.filter((t) => t.status === 'done' && t.assignee);
  const days: WeeklyDay[] = Array.from({ length: 7 }).map((_, d) => {
    const picks =
      finished.length > 0
        ? Array.from({ length: 4 }).map((_, i) => finished[(d * 4 + i) % finished.length]!)
        : [];
    const byUser = new Map<string, WeeklyDay['users'][number]>();
    picks.forEach((t, i) => {
      const assignee = t.assignee!;
      const entry = byUser.get(assignee.id) ?? {
        user: { id: assignee.id, name: assignee.name, avatarUrl: null },
        changes: [],
      };
      entry.changes.push({
        at: isoDateTime(-6 + d, 10 + i),
        from: 'TODO',
        to: 'DONE',
        task: {
          id: t.id,
          identifier: t.identifier,
          title: t.title,
          url: null,
          projectName: t.projectName,
          source: 'project' as const,
          priorityLevel: t.priority,
          dueDate: t.dueDate,
          statusLabel: t.statusLabel,
          statusColor: t.statusColor,
          statusCategory: t.statusCategory,
        },
      });
      byUser.set(assignee.id, entry);
    });
    return { date: isoDate(-6 + d), weekday: d, users: [...byUser.values()], total: picks.length };
  });

  const done = allTasks.filter((t) => t.status === 'done').length;
  return {
    weekStart: isoDate(-6),
    weekEnd: isoDate(0),
    weekParam: '',
    isCurrentWeek: true,
    stats: {
      totalChanged: 48,
      done,
      inProgress: allTasks.filter((t) => t.status === 'in_progress').length,
      inReview: allTasks.filter((t) => t.status === 'in_review').length,
      qa: 0,
      todo: allTasks.filter((t) => t.status === 'todo').length,
      blocked: 3,
      newlyStarted: 14,
    },
    users,
    days,
  };
}

// --- Email summary (CLE-233) -----------------------------------------------

export function buildEmailSummary(_userId: string): EmailSummary {
  const senders = PEOPLE_REFS.slice(0, 6);
  const subjects = [
    'Re: Status proiect E-commerce',
    'Factură nouă de la AWS România',
    'Întrebare despre Mobile Banking App',
    'Propunere colaborare TradeHub',
    'Confirmare ședință de mâine',
    'Feedback pe design Analytics Dashboard',
    'Reminder: deadline Corporate Website',
    'Contract semnat — PeopleFirst',
    'Solicitare ofertă FoodFast',
    'Update DevOps — deployment reușit',
    'Întrebare facturare LogiTrans',
    'Newsletter săptămânal echipă',
  ];
  return {
    date: isoDate(0),
    connected: true,
    isOwner: true,
    invoiceCount: 2,
    total: subjects.length,
    insights: {
      executiveSummary:
        '12 emailuri noi astăzi: 2 facturi de procesat, 3 solicitări de la clienți și o propunere de colaborare de valoare mare (TradeHub, 80k EUR).',
      suggestedActions: [
        'Procesează factura AWS România',
        'Răspunde la propunerea TradeHub',
        'Confirmă ședința cu FinTech Partners',
      ],
      alerts: ['Deadline Corporate Website în 7 zile'],
    },
    emails: subjects.map((subject, i) => {
      const sender = senders[i % senders.length];
      const isInvoice = i === 1 || i === 10;
      return {
        id: `email-${i}`,
        gmailMessageId: `gmail-${i}`,
        gmailThreadId: `thread-${i}`,
        subject,
        fromName: sender.name,
        fromEmail: `${sender.name.toLowerCase().replace(/[^a-z]/g, '.')}@example.ro`,
        receivedAt: isoDateTime(0, 8 + (i % 10)),
        isUnread: i < 5,
        summary: `Rezumat simulat pentru "${subject}".`,
        context: 'Email generat în Test Mode.',
        urgency: i < 2 ? 'high' : i < 6 ? 'medium' : 'low',
        suggestedActions: isInvoice ? ['Procesează factura'] : ['Răspunde'],
        invoice: isInvoice
          ? {
              vendor: i === 1 ? 'AWS România' : 'LogiTrans SA',
              hint: 'PDF atașat',
              attachment: null,
            }
          : null,
        attachments: [],
      };
    }),
  };
}

// --- Firmio AI conversation history (CLE-45, CLE-276) -----------------------
//
// Five hand-authored, hardcoded conversations shown in Test Mode. Each one is a
// realistic finance/ops question answered by Firmio AI with rich inline charts.
// Charts are emitted as fenced ```firmio-chart blocks carrying a JSON spec; the
// chat Markdown renderer (components/chat/Markdown.tsx) turns those into CSS/SVG
// charts via components/chat/ChatCharts. Keeping turns as plain markdown strings
// means the same renderer path serves both these seeds and any live response.

/** Helper: build a ```firmio-chart fenced block from a spec object. */
function chart(spec: unknown): string {
  return '```firmio-chart\n' + JSON.stringify(spec) + '\n```';
}

interface ConvoSeed {
  id: string;
  title: string;
  turns: ChatMessage[];
}

const CONVO_SEEDS: ConvoSeed[] = [
  // --- Chat 1: Status financiar luna aceasta -------------------------------
  {
    id: 'conv1',
    title: 'Status financiar luna aceasta',
    turns: [
      { role: 'user', content: 'Care e statusul financiar luna aceasta?' },
      {
        role: 'assistant',
        content: [
          '## Status financiar — Iunie 2026',
          'Luna arată solid: profit net **23.400 EUR** la o marjă de **49.6%**.',
          chart({
            type: 'kpi',
            items: [
              {
                label: 'Venituri',
                value: '47.200 EUR',
                delta: '+12% vs luna trecută',
                tone: 'positive',
              },
              {
                label: 'Cheltuieli',
                value: '23.800 EUR',
                delta: '-3% vs luna trecută',
                tone: 'positive',
              },
              { label: 'Profit net', value: '23.400 EUR', tone: 'positive' },
              { label: 'Marjă', value: '49.6%', tone: 'neutral' },
            ],
          }),
          '### Top 5 proiecte după venit',
          chart({
            type: 'bar',
            title: 'Venit pe proiect (EUR)',
            items: [
              { label: 'Mobile Banking', value: 14200, caption: '14.200 EUR', tone: 'neutral' },
              { label: 'JETURO ERP', value: 11800, caption: '11.800 EUR', tone: 'neutral' },
              { label: 'ADNV Platform', value: 9400, caption: '9.400 EUR', tone: 'neutral' },
              { label: 'Portal Clienți', value: 6900, caption: '6.900 EUR', tone: 'neutral' },
              { label: 'Mentenanță', value: 4900, caption: '4.900 EUR', tone: 'neutral' },
            ],
          }),
          '### Comparație vs luna trecută',
          '- Venituri: **+12%** (de la 42.100 EUR)',
          '- Cheltuieli: **-3%** (de la 24.500 EUR)',
          '- Profitul net a crescut cu **~5.000 EUR** față de mai.',
          '### Status TVA',
          chart({
            type: 'kpi',
            items: [
              { label: 'TVA colectat', value: '8.960 EUR', tone: 'neutral' },
              { label: 'TVA deductibil', value: '4.522 EUR', tone: 'neutral' },
              { label: 'TVA de plată', value: '4.438 EUR', tone: 'negative' },
            ],
          }),
          'Scadența TVA este **25 iulie**. Recomand să rezervi cei **4.438 EUR** din contul curent.',
        ].join('\n\n'),
      },
    ],
  },

  // --- Chat 2: Alocare echipă pe proiecte ----------------------------------
  {
    id: 'conv2',
    title: 'Alocare echipă pe proiecte',
    turns: [
      { role: 'user', content: 'Arată-mi alocarea echipei pe proiecte' },
      {
        role: 'assistant',
        content: [
          '## Alocare echipă',
          'Iată cum este distribuit timpul fiecărei persoane pe proiectele active. Fiecare bară reprezintă **100%** din timpul săptămânal.',
          chart({
            type: 'stacked',
            title: 'Alocare timp per persoană',
            rows: [
              {
                label: 'Fabian',
                flag: '120% — supra-alocat',
                segments: [
                  { label: 'Mobile Banking', value: 60 },
                  { label: 'JETURO ERP', value: 40 },
                  { label: 'ADNV Platform', value: 20 },
                ],
              },
              {
                label: 'Bianca',
                flag: '110% — supra-alocat',
                segments: [
                  { label: 'JETURO ERP', value: 50 },
                  { label: 'Portal Clienți', value: 40 },
                  { label: 'Mobile Banking', value: 20 },
                ],
              },
              {
                label: 'Cristina',
                segments: [
                  { label: 'ADNV Platform', value: 60 },
                  { label: 'Portal Clienți', value: 30 },
                ],
              },
              {
                label: 'Albert',
                segments: [
                  { label: 'Mentenanță', value: 40 },
                  { label: 'ADNV Platform', value: 20 },
                ],
              },
            ],
          }),
          '### Detaliu pe persoană',
          '- **Fabian** — 3 proiecte: Mobile Banking (60%), JETURO ERP (40%), ADNV Platform (20%) — *supra-alocat 120%*',
          '- **Bianca** — 3 proiecte: JETURO ERP (50%), Portal Clienți (40%), Mobile Banking (20%) — *supra-alocat 110%*',
          '- **Cristina** — 2 proiecte: ADNV Platform (60%), Portal Clienți (30%) — 90%',
          '- **Albert** — 2 proiecte: Mentenanță (40%), ADNV Platform (20%) — 60%',
          '### Distribuția echipei pe proiecte',
          chart({
            type: 'donut',
            title: 'Persoane × proiect (pondere efort)',
            slices: [
              { label: 'Mobile Banking', value: 100 },
              { label: 'JETURO ERP', value: 90 },
              { label: 'ADNV Platform', value: 100 },
              { label: 'Portal Clienți', value: 70 },
              { label: 'Mentenanță', value: 40 },
            ],
          }),
          'Două persoane sunt peste 100%. Vezi conversația despre supra-alocare pentru recomandări de reechilibrare.',
        ].join('\n\n'),
      },
    ],
  },

  // --- Chat 3: Facturi neîncasate ------------------------------------------
  {
    id: 'conv3',
    title: 'Facturi neîncasate',
    turns: [
      { role: 'user', content: 'Ce facturi am neîncasate?' },
      {
        role: 'assistant',
        content: [
          '## Facturi neîncasate',
          'Total de încasat: **34.500 EUR** pe 6 facturi. Cele marcate cu roșu sunt **depășite ca scadență**.',
          chart({
            type: 'table',
            title: 'Facturi deschise',
            headers: ['Factură', 'Client', 'Sumă', 'Emisă', 'Scadență', 'Întârziere'],
            rows: [
              {
                tone: 'danger',
                cells: ['#2041', 'JETURO SRL', '12.000 EUR', '20 mai', '27 mai', '23 zile'],
              },
              {
                tone: 'danger',
                cells: ['#2052', 'ADNV Tech', '7.400 EUR', '28 mai', '4 iun', '15 zile'],
              },
              {
                tone: 'danger',
                cells: ['#2058', 'Portal Media', '4.100 EUR', '1 iun', '8 iun', '11 zile'],
              },
              {
                tone: 'warning',
                cells: ['#2067', 'Nordic Soft', '5.500 EUR', '10 iun', '24 iun', 'în 5 zile'],
              },
              {
                tone: 'warning',
                cells: ['#2071', 'BluePeak', '3.000 EUR', '12 iun', '26 iun', 'în 7 zile'],
              },
              {
                tone: 'warning',
                cells: ['#2074', 'JETURO SRL', '2.500 EUR', '14 iun', '28 iun', 'în 9 zile'],
              },
            ],
          }),
          '### Top 3 clienți cu restanțe',
          chart({
            type: 'bar',
            title: 'Restanță pe client (EUR)',
            items: [
              { label: 'JETURO SRL', value: 14500, caption: '14.500 EUR', tone: 'negative' },
              { label: 'ADNV Tech', value: 7400, caption: '7.400 EUR', tone: 'negative' },
              { label: 'Nordic Soft', value: 5500, caption: '5.500 EUR', tone: 'neutral' },
            ],
          }),
          chart({
            type: 'alert',
            tone: 'danger',
            text: 'Contactează JETURO SRL — 12.000 EUR restanță de 23 zile (factura #2041).',
          }),
          'Pot pregăti un email de reminder către JETURO SRL dacă dorești.',
        ].join('\n\n'),
      },
    ],
  },

  // --- Chat 4: Următoarele plăți -------------------------------------------
  {
    id: 'conv4',
    title: 'Următoarele plăți',
    turns: [
      { role: 'user', content: 'Ce plăți trebuie să efectuăm?' },
      {
        role: 'assistant',
        content: [
          '## Plăți următoarele 30 de zile',
          'Total de plată: **18.200 EUR**. Soldul contului acum este **41.600 EUR**.',
          chart({
            type: 'timeline',
            title: 'Plăți grupate pe săptămână',
            groups: [
              {
                label: 'Săpt. 1 (20–26 iun)',
                items: [
                  { label: 'Hosting AWS', value: '1.200 EUR' },
                  { label: 'Licențe SaaS', value: '900 EUR' },
                ],
              },
              {
                label: 'Săpt. 2 (27 iun–3 iul)',
                items: [
                  { label: 'Salarii parțial', value: '6.800 EUR' },
                  { label: 'Contabilitate', value: '600 EUR' },
                ],
              },
              {
                label: 'Săpt. 3 (4–10 iul)',
                items: [
                  { label: 'Furnizor design', value: '2.400 EUR' },
                  { label: 'Birou / utilități', value: '1.100 EUR' },
                ],
              },
              {
                label: 'Săpt. 4 (11–17 iul)',
                items: [
                  { label: 'TVA de plată', value: '4.438 EUR' },
                  { label: 'Diverse', value: '762 EUR' },
                ],
              },
            ],
          }),
          chart({
            type: 'table',
            title: 'Detaliu plăți',
            headers: ['Furnizor', 'Sumă', 'Scadență', 'Categorie', 'Status'],
            rows: [
              {
                tone: 'warning',
                cells: ['AWS Hosting', '1.200 EUR', '23 iun', 'Infrastructură', 'Neplătit'],
              },
              { tone: 'warning', cells: ['Atlassian', '900 EUR', '25 iun', 'Licențe', 'Neplătit'] },
              {
                tone: 'neutral',
                cells: ['Salarii', '6.800 EUR', '30 iun', 'Personal', 'Programat'],
              },
              {
                tone: 'neutral',
                cells: ['Studio Design', '2.400 EUR', '7 iul', 'Subcontractor', 'Programat'],
              },
              {
                tone: 'neutral',
                cells: ['ANAF — TVA', '4.438 EUR', '25 iul', 'Taxe', 'Programat'],
              },
            ],
          }),
          '### Proiecție cash flow',
          'Sold curent **41.600 EUR** − plăți **18.200 EUR** = **23.400 EUR** după achitarea tuturor. Rămâi confortabil pe plus.',
          chart({
            type: 'alert',
            tone: 'warning',
            text: 'Plătește facturile de hosting (AWS + Atlassian) până vineri — evită penalizări de întârziere.',
          }),
        ].join('\n\n'),
      },
    ],
  },

  // --- Chat 5: Echipă — supra/sub-alocare ----------------------------------
  {
    id: 'conv5',
    title: 'Echipă — supra/sub-alocare',
    turns: [
      { role: 'user', content: 'Sunt oameni din echipă care lucrează prea mult?' },
      {
        role: 'assistant',
        content: [
          '## Analiză încărcare echipă',
          chart({
            type: 'alert',
            tone: 'danger',
            text: '2 persoane supra-alocate, 1 persoană sub-alocată.',
          }),
          '### Persoane supra-alocate',
          '**Fabian** — 9 task-uri active, 3 proiecte, ~46 ore/săptămână estimate.',
          chart({
            type: 'compare',
            title: 'Fabian — capacitate vs încărcare (ore/săpt)',
            rows: [{ label: 'Fabian', value: 46, max: 40, caption: '46 / 40 ore' }],
          }),
          '**Bianca** — 7 task-uri active, 3 proiecte, ~44 ore/săptămână estimate.',
          chart({
            type: 'compare',
            title: 'Bianca — capacitate vs încărcare (ore/săpt)',
            rows: [{ label: 'Bianca', value: 44, max: 40, caption: '44 / 40 ore' }],
          }),
          chart({
            type: 'alert',
            tone: 'info',
            text: 'Redistribuie 2 task-uri de la Fabian către Albert pentru a reveni sub 40h.',
          }),
          '### Persoană sub-alocată',
          '**Albert** — 3 task-uri active, ~24 ore/săptămână, **~16 ore libere** estimate.',
          chart({
            type: 'compare',
            title: 'Albert — capacitate vs încărcare (ore/săpt)',
            rows: [{ label: 'Albert', value: 24, max: 40, caption: '24 / 40 ore' }],
          }),
          chart({
            type: 'alert',
            tone: 'info',
            text: 'Albert are capacitate — poate prelua task-uri de pe ADNV Platform.',
          }),
        ].join('\n\n'),
      },
    ],
  },
];

export function buildConversationSummaries(): ConversationSummary[] {
  return CONVO_SEEDS.map((c, i) => {
    const last = c.turns[c.turns.length - 1];
    const firstUser = c.turns.find((t) => t.role === 'user');
    return {
      id: c.id,
      userId: 'p1',
      title: c.title,
      pinned: i === 0,
      createdAt: isoDateTime(-(i + 1) * 2, 10),
      updatedAt: isoDateTime(-(i + 1), 15),
      messageCount: c.turns.length,
      preview: (firstUser ?? last)?.content.slice(0, 80) ?? '',
    };
  });
}

export function buildConversation(id: string): Conversation | null {
  const seed = CONVO_SEEDS.find((c) => c.id === id);
  if (!seed) return null;
  return {
    id: seed.id,
    userId: 'p1',
    title: seed.title,
    pinned: false,
    createdAt: isoDateTime(-2, 10),
    updatedAt: isoDateTime(-1, 15),
    turns: seed.turns,
  };
}

// --- Daily status check-ins ---------------------------------------------------
// The demo board: the person's open tasks, a few already checked in so every
// quick status (and the "status necompletat" flag) is visible at a glance.
// Writes are remembered in memory for the session so a click sticks.

const DEMO_CHECK_INS = new Map<
  string,
  { status: CheckInStatus; comment: string | null; attachments?: CommentAttachment[] }
>();

const SCRIPTED_CHECK_INS: Array<[number, CheckInStatus, string | null]> = [
  [0, 'DONE', 'Livrat pe staging, aștept review.'],
  [1, 'IN_PROGRESS', 'Mai am de scris testele.'],
  [2, 'BLOCKED', 'Aștept accesul la API-ul clientului.'],
];

function demoUserId(userId?: string): string {
  const people = buildPeople();
  return people.find((p) => p.id === userId)?.id ?? people[0].id;
}

function boardTasksFor(userId: string): TodayTask[] {
  const all = buildTasks();
  let mine = all.filter((t) => t.assigneeId === userId && t.statusCategory !== 'CANCELLED');
  if (mine.length === 0) mine = all.slice(0, 6);
  return mine.slice(0, 8).map((t, index) => {
    const daily = toDailyTask(t);
    const scripted = SCRIPTED_CHECK_INS.find(([i]) => i === index);
    const remembered = DEMO_CHECK_INS.get(`${userId}:${t.id}`);
    const checkIn = remembered ?? (scripted ? { status: scripted[1], comment: scripted[2] } : null);
    const reasons: TodayTask['reasons'] = [];
    if (t.dueDate && t.dueDate.slice(0, 10) < isoDate(0)) reasons.push('overdue');
    else if (t.dueDate && t.dueDate.slice(0, 10) === isoDate(0)) reasons.push('due_today');
    if (t.statusCategory === 'STARTED') reasons.push('started');
    if (reasons.length === 0) reasons.push('worked_on');
    if (checkIn) reasons.push('checked_in');
    return {
      ...daily,
      reasons,
      needsStatus: !checkIn && !reasons.includes('worked_on'),
      hoursToday: index % 3 === 0 ? 1.5 : index % 3 === 1 ? 0.5 : 0,
      timerRunning: false,
      checkIn: checkIn
        ? {
            status: checkIn.status,
            taskStatusId: checkIn.status === 'BLOCKED' ? null : t.statusId,
            comment: checkIn.comment,
            commentId: checkIn.comment ? `demo-comment-${t.id}` : null,
            attachments: checkIn.attachments ?? [],
            updatedAt: isoDateTime(0, 16, 40),
          }
        : null,
    };
  });
}

export function buildDailyStatusBoard(userId?: string, date?: string): DailyStatusBoard {
  const id = demoUserId(userId);
  const person = buildPeople().find((p) => p.id === id)!;
  const day = date ?? isoDate(0);
  const tasks = boardTasksFor(id);
  const checkedIn = tasks.filter((t) => t.checkIn).length;
  return {
    user: { id, name: person.fullName, email: person.email },
    day,
    editable: day === isoDate(0),
    tasks,
    pending: tasks.filter((t) => t.needsStatus).length,
    checkedIn,
    schedule: {
      enabled: true,
      reminderTime: '17:30',
      reportTime: '18:00',
      timeZone: 'Europe/Bucharest',
      reminderFiredAt: null,
    },
  };
}

const SECTION_OF: Record<CheckInStatus, ReportSection> = {
  DONE: 'done',
  IN_PROGRESS: 'inProgress',
  BLOCKED: 'blocked',
  NOT_STARTED: 'notStarted',
};

function reportFromBoard(board: DailyStatusBoard): DailyStatusReport {
  const sections: Record<ReportSection, ReportTask[]> = {
    done: [],
    inProgress: [],
    blocked: [],
    notStarted: [],
  };
  for (const task of board.tasks) {
    const section: ReportSection = task.checkIn
      ? SECTION_OF[task.checkIn.status]
      : task.statusCategory === 'COMPLETED'
        ? 'done'
        : task.statusCategory === 'STARTED' || task.hoursToday > 0
          ? 'inProgress'
          : 'notStarted';
    sections[section].push({ ...task, section, unreported: !task.checkIn });
  }
  const all = Object.values(sections).flat();
  const count = (s: ReportSection) => sections[s].length;
  const summary = {
    total: all.length,
    checkedIn: all.filter((t) => !t.unreported).length,
    unreported: all.filter((t) => t.unreported).length,
    done: count('done'),
    inProgress: count('inProgress'),
    blocked: count('blocked'),
    notStarted: count('notStarted'),
    hoursToday: Math.round(all.reduce((sum, t) => sum + t.hoursToday, 0) * 100) / 100,
    personal: 0,
  };
  const lines = [`## ${board.user.name} — ${board.day}`, ''];
  const label: Record<ReportSection, string> = {
    done: '✅ Finalizate',
    inProgress: '🔄 În lucru',
    blocked: '🚫 Blocate',
    notStarted: '⬜ Neîncepute',
  };
  for (const section of ['done', 'inProgress', 'blocked', 'notStarted'] as ReportSection[]) {
    if (sections[section].length === 0) continue;
    lines.push(`### ${label[section]} (${sections[section].length})`);
    for (const t of sections[section]) {
      const extra = t.checkIn?.comment
        ? ` — ${t.checkIn.comment}`
        : t.unreported
          ? ' — _status necompletat_'
          : '';
      lines.push(`- ${t.identifier ?? ''} ${t.title}${extra}`.trim());
    }
    lines.push('');
  }
  return {
    user: board.user,
    day: board.day,
    generatedAt: isoDateTime(0, 18, 0),
    sections,
    summary,
    persisted: board.day < isoDate(0),
    markdown: lines.join('\n'),
  };
}

export function buildDailyStatusReport(userId?: string, date?: string): DailyStatusReport {
  return reportFromBoard(buildDailyStatusBoard(userId, date));
}

export function buildTeamDailyReport(date?: string): TeamDailyStatusReport {
  const day = date ?? isoDate(0);
  const members = buildPeople()
    .map((p) => reportFromBoard(buildDailyStatusBoard(p.id, day)))
    .filter((r) => r.summary.total > 0)
    .map((r) => ({
      user: r.user,
      summary: r.summary,
      sections: r.sections,
      notReported: r.summary.unreported > 0,
      persisted: r.persisted,
    }));
  const totals = {
    members: members.length,
    reported: members.filter((m) => !m.notReported).length,
    notReported: members.filter((m) => m.notReported).length,
    done: members.reduce((s, m) => s + m.summary.done, 0),
    inProgress: members.reduce((s, m) => s + m.summary.inProgress, 0),
    blocked: members.reduce((s, m) => s + m.summary.blocked, 0),
    notStarted: members.reduce((s, m) => s + m.summary.notStarted, 0),
    hoursToday: Math.round(members.reduce((s, m) => s + m.summary.hoursToday, 0) * 100) / 100,
  };
  const idle = buildPeople()
    .filter((p) => !members.some((m) => m.user.id === p.id))
    .map((p) => ({ id: p.id, name: p.fullName, email: p.email }));
  return {
    day,
    generatedAt: isoDateTime(0, 18, 0),
    members,
    idle,
    totals,
    markdown: `# Raport zilnic echipă — ${day}\n\n${members.map((m) => `## ${m.user.name}`).join('\n')}\n`,
  };
}

export function buildDailyReportHistory(): DailyReportHistoryItem[] {
  return [1, 2, 3, 4, 5].map((daysAgo) => ({
    day: isoDate(-daysAgo),
    totalTasks: 5 + (daysAgo % 3),
    checkedIn: 4 + (daysAgo % 2),
    doneCount: 2 + (daysAgo % 2),
    blockedCount: daysAgo % 4 === 0 ? 1 : 0,
    generatedBy: 'scheduler',
    emailSentAt: isoDateTime(-daysAgo, 18, 1),
    createdAt: isoDateTime(-daysAgo, 18, 0),
  }));
}

export function buildDailyStatusSettings(): DailyStatusSettings {
  const people = buildPeople();
  return {
    config: {
      enabled: true,
      reminderTime: '17:30',
      reportTime: '18:00',
      reminderSenderId: null,
      timeZone: 'Europe/Bucharest',
    },
    schedule: {
      armed: true,
      enabled: true,
      reminderTime: '17:30',
      reportTime: '18:00',
      timeZone: 'Europe/Bucharest',
      nextReminderAt: isoDateTime(0, 17, 30),
      nextReportAt: isoDateTime(0, 18, 0),
      lastReminder: {
        at: isoDateTime(-1, 17, 30),
        day: isoDate(-1),
        ok: true,
        outcome: 'ran',
        summary: '2 persoane de reamintit, 2 emailuri, 2 mesaje în chat',
        errors: [],
      },
      lastReport: {
        at: isoDateTime(-1, 18, 0),
        day: isoDate(-1),
        ok: true,
        outcome: 'ran',
        summary: '4 rapoarte, 4 emailuri individuale, 1 emailuri echipă',
        errors: [],
      },
    },
    senderCandidates: people
      .slice(0, 2)
      .map((p) => ({ id: p.id, name: p.fullName, role: 'ADMIN' })),
  };
}

/** Test Mode writes for /api/daily-status/me/tasks/:taskId (PUT / DELETE). */
export function mockDailyStatusWrite(pathname: string, method: string, body: unknown): unknown {
  const taskId = pathname.split('/').filter(Boolean).pop() ?? '';
  const userId = demoUserId();
  if (method === 'DELETE') {
    DEMO_CHECK_INS.delete(`${userId}:${taskId}`);
    return { deleted: true };
  }
  const input = (body ?? {}) as {
    status?: CheckInStatus;
    comment?: string | null;
    attachments?: CommentAttachment[] | null;
  };
  if (input.status) {
    DEMO_CHECK_INS.set(`${userId}:${taskId}`, {
      status: input.status,
      comment: input.comment ?? null,
      attachments: input.attachments ?? [],
    });
  }
  const task = buildDailyStatusBoard(userId).tasks.find((t) => t.id === taskId);
  return task ?? { id: taskId, simulated: true };
}
